import { useCallback, useEffect, useRef, useState } from 'react';
import { MapView } from './components/MapView';
import { Sidebar } from './components/Sidebar';
import { HealthBadge } from './components/HealthBadge';
import { AuthMenu } from './components/AuthMenu';
import { FilterBar } from './components/FilterBar';
import { PropertyDetail } from './components/PropertyDetail';
import { ResultsList } from './components/ResultsList';
import { ResultsToolbar, type ListMode } from './components/ResultsToolbar';
import type { FlyRequest } from './components/FlyToOnRequest';
import type { PlaceSuggestion } from './lib/types';
import { api } from './lib/api';
import { MEXICO_BBOX_ARRAY, mexicoExtent } from './lib/mexico';
import {
  filtersToUrl,
  parseUrlFilters,
  parseUrlPropertyId,
  pushFiltersToUrl,
  type Filters,
} from './lib/filters';

/**
 * Zoom destino al seleccionar un place del autocomplete (TK-S02-02).
 * Estos valores los pidió el backlog explícitamente:
 *   estado    → ~7  (entidad federativa visible casi por entero)
 *   municipio → ~11 (mancha urbana del municipio)
 *   colonia   → ~14 (calles individuales legibles)
 */
const PLACE_ZOOMS: Record<PlaceSuggestion['kind'], number> = {
  estado: 7,
  municipio: 11,
  colonia: 14,
};

export default function App() {
  const [showProperties, setShowProperties] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  // Apagada por defecto: dos capas de polígonos encimadas se leen como
  // un solo color embarrado y ninguna se entiende.
  const [showSocio, setShowSocio] = useState(false);
  const [filters, setFilters] = useState<Filters>(() => parseUrlFilters());
  // Deep-link: `?property=ID` abre el drawer al cargar y se mantiene en
  // sync con la URL, así "Copiar enlace" comparte la propiedad abierta.
  const [selectedId, setSelectedId] = useState<string | null>(() => parseUrlPropertyId());
  // Hover sync lista↔mapa: la card bajo el cursor resalta su marker y
  // viceversa. Efímero — no viaja a la URL ni dispara fetches.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Total de properties matchadas (alimentado por /v1/facets en FilterBar
  // pero también queremos exponerlo a Sidebar / aquí).
  const [matchedTotal, setMatchedTotal] = useState<number | null>(null);
  // Comando explícito de flyTo al seleccionar un lugar del autocomplete.
  // El nonce monotónico permite re-triggerear el mismo target sin que
  // React deduplique por shallow equality.
  const [flyRequest, setFlyRequest] = useState<FlyRequest | null>(null);
  // Panel de la lista: cerrado por defecto para no tapar el mapa. null =
  // cerrado; 'results' = resultados de la búsqueda; 'watch' = observados.
  const [list, setList] = useState<ListMode | null>(null);
  const toggleList = useCallback((mode: ListMode) => setList((cur) => (cur === mode ? null : mode)), []);
  const closeList = useCallback(() => setList(null), []);
  // Cualquier parte de la app puede abrir la lista (la bienvenida de una
  // cuenta nueva termina ahí: es donde se ve el valor del producto).
  useEffect(() => {
    const onOpenList = () => setList('results');
    window.addEventListener('tk:open-list', onOpenList);
    return () => window.removeEventListener('tk:open-list', onOpenList);
  }, []);
  const mapAreaRef = useRef<HTMLDivElement>(null);

  // Lo que flota sobre el mapa se publica como variables CSS en .map-area:
  //  --filterbar-h → el zoom de Leaflet se acomoda debajo de la barra.
  //  --results-w   → al encuadrar resultados, nada queda bajo la lista.
  // La barra cambia de alto al plegar filtros o al angostar la ventana.
  useEffect(() => {
    const area = mapAreaRef.current;
    if (!area) return;
    const bar = area.querySelector<HTMLElement>('.filter-bar');
    const panel = area.querySelector<HTMLElement>('.results-list');
    const update = () => {
      area.style.setProperty('--filterbar-h', `${Math.ceil(bar?.getBoundingClientRect().height ?? 0)}px`);
      // En celular la lista ocupa todo el ancho: no hay margen que valga.
      const w = panel && window.innerWidth > 768 ? Math.ceil(panel.getBoundingClientRect().width) + 12 : 0;
      area.style.setProperty('--results-w', `${w}px`);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    if (bar) ro.observe(bar);
    if (panel) ro.observe(panel);
    return () => ro.disconnect();
  }, [list]);

  function handleSelectPlace(p: PlaceSuggestion) {
    setFlyRequest({
      nonce: Date.now(),
      bbox: p.bbox,
      center: p.center,
      zoom: PLACE_ZOOMS[p.kind],
    });
  }

  // Identidad estable: PropertyDetail ata su listener de Esc a [onClose] —
  // con el closure inline se re-ataba en CADA render de App (hover incluido).
  const handleCloseDetail = useCallback(() => setSelectedId(null), []);

  // Re-fetch del total cada vez que filtros cambian.
  useEffect(() => {
    const ctrl = new AbortController();
    api.facets(filters, ctrl.signal)
      .then((f) => setMatchedTotal(f.total))
      .catch(() => {});
    return () => ctrl.abort();
  }, [filters]);

  // URL sync. Dos matices:
  //  - Comparación por la forma SERIALIZADA (filtersToUrl), no por
  //    JSON.stringify de los objetos: la igualdad por JSON dependía del
  //    orden de keys y de keys-con-undefined.
  //  - Abrir/cerrar propiedad = pushState (el botón Atrás cierra el
  //    drawer, navegación real). Ajustar filtros = replaceState (antes
  //    cada tecleo apilaba una entrada y Atrás requería N clicks).
  useEffect(() => {
    const filtersChanged = filtersToUrl(parseUrlFilters()) !== filtersToUrl(filters);
    const propertyChanged = parseUrlPropertyId() !== selectedId;
    if (!filtersChanged && !propertyChanged) return;
    pushFiltersToUrl(filters, selectedId, propertyChanged ? 'push' : 'replace');
  }, [filters, selectedId]);

  // Al entrar con deep-link (?property=ID), centramos el mapa en la
  // propiedad compartida (si tiene coords). Solo al mount: navegaciones
  // posteriores ya las maneja PropertyMarkers con su fly condicional.
  useEffect(() => {
    const id = parseUrlPropertyId();
    if (!id) return;
    const ctrl = new AbortController();
    api.propertyDetail(id, ctrl.signal)
      .then((d) => {
        if (!d.coords_geojson) return;
        const g = JSON.parse(d.coords_geojson) as
          | { type?: string; coordinates?: [number, number] }
          | null;
        if (g?.type === 'Point' && Array.isArray(g.coordinates)) {
          const [lng, lat] = g.coordinates;
          if (Number.isFinite(lng) && Number.isFinite(lat)) {
            setFlyRequest({ nonce: Date.now(), center: [lng, lat], zoom: 14 });
          }
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  // Listener de back/forward del browser → revertimos filtros + drawer.
  useEffect(() => {
    const onPop = () => {
      setFilters(parseUrlFilters());
      setSelectedId(parseUrlPropertyId());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // "Centrar en resultados": trae hasta 500 features del set filtrado
  // (sin bbox), computa el bounding box y dispara un flyToBounds. Si
  // ningún resultado tiene coords, no hace nada.
  async function handleCenterResults() {
    try {
      // Sólo con sesión: sin ella no hay puntos en el mapa (2026-10-08).
      const fc = await api.propertiesGeoJson({ filters, limit: 500 });
      // Sólo puntos dentro de México: una coordenada mala de una fuente
      // (longitud sin signo) estiraba la caja hasta el Atlántico y el mapa
      // quedaba gris, en medio del océano.
      const ext = mexicoExtent(
        fc.features.flatMap((f) => (f.geometry ? [f.geometry.coordinates as [number, number]] : [])),
      );
      if (!ext) return;
      setFlyRequest({ nonce: Date.now(), bbox: ext.bbox, zoom: 13 });
    } catch {
      // best-effort: un fallo de red no debe romper la UI.
    }
  }

  const handleFitMexico = useCallback(() => {
    setFlyRequest({ nonce: Date.now(), bbox: MEXICO_BBOX_ARRAY, zoom: 6 });
  }, []);

  return (
    <div className="app">
      <header className="header">
        <h1>Terra<span>Kairos</span></h1>
        <a className="header-site-link" href="https://www.terrakairos.mx/" title="Ir al sitio de TerraKairos">← Sitio</a>
        <HealthBadge />
        <AuthMenu />
      </header>
      <Sidebar
        showProperties={showProperties}
        setShowProperties={setShowProperties}
        showHeatmap={showHeatmap}
        setShowHeatmap={setShowHeatmap}
        showSocio={showSocio}
        setShowSocio={setShowSocio}
        matchedTotal={matchedTotal}
      />
      <div className={`map-area${list ? ' list-open' : ''}`} ref={mapAreaRef}>
        {/* Una sola columna flota sobre el mapa: la barra y, debajo, la
            lista. Antes la lista iba fija a 116 px del borde y la barra —
            que crece al partirse en renglones — le tapaba la cabecera con
            "Copiar enlace" y "Exportar CSV". */}
        <div className="map-overlay">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            matchedTotal={matchedTotal}
            onSelectPlace={handleSelectPlace}
            actions={
              <ResultsToolbar
                filters={filters}
                listOpen={list !== null}
                listMode={list ?? 'results'}
                onToggleList={toggleList}
              />
            }
          />
          {list && (
            <ResultsList
              filters={filters}
              mode={list}
              selectedId={selectedId}
              hoveredId={hoveredId}
              onSelect={setSelectedId}
              onHover={setHoveredId}
              onClearFilters={() => setFilters({})}
              onSortChange={(sort) => setFilters((f) => {
                const next = { ...f };
                if (sort) next.sort = sort; else delete next.sort;
                return next;
              })}
              onClose={closeList}
            />
          )}
        </div>
        <MapView
          showProperties={showProperties}
          showHeatmap={showHeatmap}
          showSocio={showSocio}
          filters={filters}
          onSelectProperty={setSelectedId}
          selectedId={selectedId}
          matchedTotal={matchedTotal}
          flyRequest={flyRequest}
          onCenterResults={handleCenterResults}
          onFitMexico={handleFitMexico}
          hoveredId={hoveredId}
          onHover={setHoveredId}
        />
      </div>
      <PropertyDetail propertyId={selectedId} onClose={handleCloseDetail} />
    </div>
  );
}
